import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { SUPPRESS_GLOBAL_ERROR_DIALOG } from '../error-interceptor';
import { Tournament, TournamentPublicGameStats } from './tournament.model';

const API = `${environment.apiUrl}/tournaments`;

@Injectable({ providedIn: 'root' })
export class TournamentService {
  constructor(private http: HttpClient) {}

  list(): Observable<{ tournaments: Tournament[] }> {
    return this.http.get<{ tournaments: Tournament[] }>(API);
  }

  adminList(suppressGlobalErrorDialog = false): Observable<{ tournaments: Tournament[] }> {
    return this.http.get<{ tournaments: Tournament[] }>(`${API}/admin`, suppressGlobalErrorDialog ? {
      context: new HttpContext().set(SUPPRESS_GLOBAL_ERROR_DIALOG, true),
    } : {});
  }

  getPublicGameStats(tournamentId: string, scheduleId: string): Observable<TournamentPublicGameStats> {
    return this.http.get<TournamentPublicGameStats>(`${API}/${tournamentId}/games/${scheduleId}/stats`, {
      context: new HttpContext().set(SUPPRESS_GLOBAL_ERROR_DIALOG, true),
    });
  }

  create(payload: Partial<Tournament>): Observable<{ tournament: Tournament }> {
    return this.http.post<{ tournament: Tournament }>(API, payload);
  }

  update(id: string, payload: Partial<Tournament>): Observable<{ tournament: Tournament }> {
    return this.http.patch<{ tournament: Tournament }>(`${API}/${id}`, payload);
  }

  delete(id: string) {
    return this.http.delete<{ message: string }>(`${API}/${id}`);
  }

  add(id: string, kind: 'divisions' | 'gyms' | 'teams' | 'games', payload: Record<string, unknown>) {
    return this.http.post<{ tournament: Tournament }>(`${API}/${id}/${kind}`, payload);
  }

  updateDivision(id: string, divisionId: string, payload: Record<string, unknown>) {
    return this.http.patch<{ tournament: Tournament }>(`${API}/${id}/divisions/${divisionId}`, payload);
  }

  updateGame(id: string, gameId: string, payload: Record<string, unknown>) {
    return this.http.patch<{ tournament: Tournament }>(`${API}/${id}/games/${gameId}`, payload);
  }

  importSchedule(id: string, rows: Record<string, unknown>[]) {
    return this.http.post<{ tournament: Tournament; created: { divisions: number; teams: number; games: number } }>(`${API}/${id}/games/import`, { rows });
  }

  remove(id: string, kind: 'divisions' | 'gyms' | 'teams' | 'games', itemId: string) {
    return this.http.delete<{ tournament: Tournament }>(`${API}/${id}/${kind}/${itemId}`);
  }

  generateBracket(id: string, divisionId: string, playoffTeamCount?: number) {
    return this.http.post<{ tournament: Tournament }>(`${API}/${id}/bracket/generate`, {
      division_id: divisionId,
      playoff_team_count: playoffTeamCount,
    });
  }

  updateTeam(id: string, teamId: string, payload: Record<string, unknown>) {
    return this.http.patch<{ tournament: Tournament }>(`${API}/${id}/teams/${teamId}`, payload);
  }

  uploadTeamLogo(id: string, teamId: string, file: File) {
    const formData = new FormData();
    formData.append('logo', file);
    return this.http.post<{ tournament: Tournament }>(`${API}/${id}/teams/${teamId}/logo`, formData);
  }

  generatePools(id: string, divisionId: string) {
    return this.http.post<{ tournament: Tournament; pools: number; games: number }>(`${API}/${id}/pools/generate`, { division_id: divisionId });
  }

  updatePools(id: string, divisionId: string, pools: { pool_name: string; team_ids: string[] }[]) {
    return this.http.put<{ tournament: Tournament }>(`${API}/${id}/pools/${divisionId}`, { pools });
  }

  scoreMatchup(id: string, matchupId: string, home_score: number | null, away_score: number | null) {
    return this.http.patch<{ tournament: Tournament }>(`${API}/${id}/bracket/matchups/${matchupId}`, { home_score, away_score });
  }

  scheduleMatchup(id: string, matchupId: string, payload: Record<string, unknown>) {
    return this.http.patch<{ tournament: Tournament }>(`${API}/${id}/bracket/matchups/${matchupId}/schedule`, payload);
  }
}
